import lunr from 'lunr';
import {
  AmbiguityStatus,
  AnalysisTypes,
  BidDraftingStatus,
  BidQualityStatus,
  ContractClauseScannerAnalysisStatus,
  CvAssessmentStatus,
  DeviationsStatus,
  EarlyPhaseAssessmentDependencyStatus,
  InconsistencyStatus,
  ProjectUserRoles,
  TenderFeedbackPositivesNegativesStatus,
} from './constants';
import { docsRegexp, exactDocRefRegexp, reduntantWhitespaceRegexp } from './regexps';
import { toastManager } from '@/app/ui/components/toast';
import { AxiosResponse } from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NULL_PROJECT } from '@/app/components/features/projects/constants';

export function parseBytesToMb(fileSize: number) {
  if (fileSize <= 0) return 0;
  return (Math.round((fileSize / 1024 / 1024 + Number.EPSILON) * 100) / 100).toFixed(2);
}

export const convertSecondsToMilliseconds = (seconds: number) => seconds * 1000;

export function getTextChunk(matchedArr: RegExpMatchArray | string[], searchText: string) {
  const textChunks = matchedArr.map((chunk, index) => ({
    chunk,
    index,
  }));

  const chunksSearchIndex = lunr(function () {
    this.field('chunk');

    textChunks.forEach((s) => {
      this.add({ chunk: s.chunk, refIndex: s.index });
    });
  });

  return chunksSearchIndex.search(searchText);
}

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export function getProjectIdParam(projectIdParam: string | typeof NULL_PROJECT) {
  return projectIdParam && projectIdParam !== NULL_PROJECT ? projectIdParam : undefined;
}
export const getIsStatusIncluded = <T>(status: T | undefined, statuses: T[]) => {
  return status ? statuses.includes(status) : false;
};

export const getAnalysisStatuses = <T extends AnalysisTypes>(analysisType: T) => {
  const statusMap = {
    [AnalysisTypes.DEVIATIONS]: {
      completed: [DeviationsStatus.DONE, DeviationsStatus.NOT_FOUND],
      inProgress: [DeviationsStatus.IN_PROGRESS],
      failed: [DeviationsStatus.FAILED],
    },
    [AnalysisTypes.INCONSISTENCIES]: {
      completed: [InconsistencyStatus.DONE, InconsistencyStatus.NOT_FOUND],
      inProgress: [InconsistencyStatus.IN_PROGRESS],
      failed: [InconsistencyStatus.FAILED],
    },
    [AnalysisTypes.SEGMENT_INCONSISTENCIES]: {
      completed: [InconsistencyStatus.DONE, InconsistencyStatus.NOT_FOUND],
      inProgress: [InconsistencyStatus.IN_PROGRESS],
      failed: [InconsistencyStatus.FAILED],
    },
    [AnalysisTypes.AMBIGUITIES]: {
      completed: [AmbiguityStatus.DONE, AmbiguityStatus.NOT_FOUND],
      inProgress: [AmbiguityStatus.IN_PROGRESS],
      failed: [AmbiguityStatus.FAILED],
    },
    [AnalysisTypes.BID_QUALITY]: {
      completed: [BidQualityStatus.DONE, BidQualityStatus.NOT_FOUND],
      inProgress: [BidQualityStatus.IN_PROGRESS],
      failed: [BidQualityStatus.FAILED],
    },
    [AnalysisTypes.CV_ASSESSMENT]: {
      completed: [
        CvAssessmentStatus.COMPLETED,
        CvAssessmentStatus.RFP_NOT_EXHAUSTIVE,
        CvAssessmentStatus.BID_NOT_EXHAUSTIVE,
      ],
      inProgress: [CvAssessmentStatus.IN_PROGRESS],
      failed: [CvAssessmentStatus.FAILED],
      missingRfp: [CvAssessmentStatus.RFP_NOT_EXHAUSTIVE],
      missingBid: [CvAssessmentStatus.BID_NOT_EXHAUSTIVE],
    },
    [AnalysisTypes.DEPENDENCIES]: {
      completed: [
        EarlyPhaseAssessmentDependencyStatus.DONE,
        EarlyPhaseAssessmentDependencyStatus.NOT_FOUND,
      ],
      inProgress: [EarlyPhaseAssessmentDependencyStatus.IN_PROGRESS],
      failed: [EarlyPhaseAssessmentDependencyStatus.FAILED],
    },
    [AnalysisTypes.TENDER_FEEDBACK_POSITIVES_NEGATIVES]: {
      completed: [
        TenderFeedbackPositivesNegativesStatus.DONE,
        TenderFeedbackPositivesNegativesStatus.NOT_FOUND,
      ],
      inProgress: [TenderFeedbackPositivesNegativesStatus.IN_PROGRESS],
      failed: [TenderFeedbackPositivesNegativesStatus.FAILED],
    },
    [AnalysisTypes.CLAUSE_SCANNER]: {
      completed: [ContractClauseScannerAnalysisStatus.DONE],
      inProgress: [ContractClauseScannerAnalysisStatus.IN_PROGRESS],
      failed: [ContractClauseScannerAnalysisStatus.FAILED],
    },
    [AnalysisTypes.BID_DRAFTING]: {
      completed: [BidDraftingStatus.DONE],
      inProgress: [BidDraftingStatus.IN_PROGRESS],
      failed: [BidDraftingStatus.FAILED],
    },
    [AnalysisTypes.WORK_PACKAGES]: {
      completed: [],
      inProgress: [],
      failed: [],
    },
  };

  return statusMap[analysisType];
};

export const getIsProjectOwner = (projectRoleName?: ProjectUserRoles) => {
  if (!projectRoleName) return false;
  return projectRoleName === ProjectUserRoles.ADMIN;
};

export const getDocumentReferenceNumbersFromMatch = (matchedString: string) =>
  matchedString
    .slice(1, -1)
    .split(',')
    .map((docRef) => docRef.trim())
    .filter((docRef) => exactDocRefRegexp.test(docRef))
    .map((docRef) => +docRef.replace('doc', ''));

export const getStatementWithoutDocs = (statement: string) => {
  return statement.replace(docsRegexp, '').replace(reduntantWhitespaceRegexp, ' ').trim();
};

export const handleReportExportSuccess = ({
  response,
  baseFileName,
}: {
  response?: AxiosResponse<Blob, any>;
  baseFileName: string;
}) => {
  if (!response) return;

  // Create blob from response data
  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream',
  });

  // Extract filename from content-disposition header if available
  const contentDisposition = response.headers['content-disposition'];
  let fileName = baseFileName;
  if (contentDisposition) {
    const matches = contentDisposition.match(/filename="?([^";]+)"?/);
    if (matches && matches.length > 1) {
      fileName = matches[1];
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Downloads a file from a URL (e.g., sasUri) with a specified filename.
 * Falls back to opening in a new tab if the fetch fails.
 */
export const downloadFileFromUrl = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error('Download failed:', error);
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      toastManager.add({
        type: 'error',
        title: 'Download failed',
        description: 'Unable to download. Please check your popup blocker settings.',
      });
    }
  }
};
